import { useEffect, useState, useRef, useCallback, useMemo, useId } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Search, X, Loader2, Zap, MapPinOff, Users as UsersIcon, TrendingDown, ChevronDown } from 'lucide-react';
import { useOrganizationStore } from '../store/organizationStore';
import { OrganizationCard } from '../components/organizations/OrganizationCard';
import { OrgTypeFilter } from '../components/organizations/OrgTypeFilter';
import { SegmentPills } from '../components/shared/SegmentPills';
import { useAuthStore } from '../store/authStore';
import { bookLabel } from '../lib/actingFor';
import type { CustomerStatus, OrganizationFilters } from '../types';

const VALID_STATES: CustomerStatus[] = ['PROSPECT', 'ONBOARDED', 'ACTIVE', 'SLIPPING', 'LAPSED'];

const CUSTOMER_STATUS_LABELS: Record<CustomerStatus, string> = {
  PROSPECT: 'Prospect',
  ONBOARDED: 'Onboarded',
  ACTIVE: 'Active customer',
  SLIPPING: 'Slipping',
  LAPSED: 'Lapsed',
};

/**
 * Organizations list page — Variant A layout
 * 1. Page header
 * 2. Scope banner ([All] [Mine] [Unassigned] + in-scope total)
 * 3. Search bar (scope-aware placeholder)
 * 4. Segment chips with in-scope counts ("Narrow X by:")
 * 5. Advanced filters <details>
 * 6. Card grid + pagination
 */
export const Organizations = () => {
  const currentUser = useAuthStore((s) => s.user);
  const {
    organizations,
    pagination,
    segment,
    counts,
    filters,
    isLoading,
    error,
    availableStates,
    segmentCounts,
    isLoadingCounts,
    fetchOrganizations,
    fetchCounts,
    fetchSegmentCounts,
    setSegment,
    setPage,
    setFilters,
    clearError,
    clearFilters,
  } = useOrganizationStore();

  // CRM-158: keep an active state selection in the options even when the current view
  // no longer offers it, so the dropdown shows the real filter and it can be cleared.
  const stateOptions = filters.state && !availableStates.includes(filters.state)
    ? [...availableStates, filters.state].sort()
    : availableStates;

  const advancedPanelId = useId();

  const [searchInput, setSearchInput] = useState(filters.search || '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [cityInput, setCityInput] = useState(filters.city || '');
  const cityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // Advanced filters disclosure: start open only if any advanced filter is set at mount time
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(() =>
    Boolean(
      filters.state ||
      filters.city ||
      filters.isPrivate !== undefined ||
      filters.ncesLevel ||
      filters.hasBlastLink !== undefined ||
      (filters.customerStatus && filters.customerStatus.length > 0),
    ),
  );

  // Keep local search in sync if filters.search is cleared from elsewhere
  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  // Keep local city input in sync if filters.city is cleared from elsewhere.
  // The store holds the trimmed term, so ignore values that only differ from
  // what is typed by surrounding whitespace — otherwise a trailing space is
  // eaten as soon as the debounce fires mid-word ("San " -> "SanDiego").
  useEffect(() => {
    setCityInput((prev) => (prev.trim() === (filters.city || '') ? prev : filters.city || ''));
  }, [filters.city]);

  // Fetch counts + organizations + segment counts on mount
  useEffect(() => {
    fetchCounts();
    fetchOrganizations();
    fetchSegmentCounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate customerStatus filter from URL on mount (run once)
  useEffect(() => {
    const param = searchParams.get('customerStatus');
    if (!param) return;
    const parsed = param
      .split(',')
      .filter((s): s is CustomerStatus => VALID_STATES.includes(s as CustomerStatus));
    if (parsed.length > 0) setFilters({ customerStatus: parsed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate scope + search from URL on mount (deep-links: global search
  // "See all results", dashboard Organizations tile). CRM-24.
  useEffect(() => {
    const next: Partial<OrganizationFilters> = {};
    const scope = searchParams.get('scope');
    if (scope === 'all') next.assignedTo = undefined;
    else if (scope === 'mine') next.assignedTo = 'me';
    else if (scope === 'unassigned') next.assignedTo = 'unassigned';
    const search = searchParams.get('search');
    if (search) { next.search = search; setSearchInput(search); }
    if (Object.keys(next).length > 0) setFilters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync customerStatus filter to URL on change (canonical enum-order, replaceState)
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const sorted = VALID_STATES.filter((s) => filters.customerStatus?.includes(s));
    const value = sorted.length > 0 ? sorted.join(',') : null;
    if (value) next.set('customerStatus', value);
    else next.delete('customerStatus');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.customerStatus]);

  // Debounced search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setFilters({ search: value || undefined });
      }, 300);
    },
    [setFilters],
  );

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setFilters({ search: undefined });
  }, [setFilters]);

  // Debounced city filter
  const handleCityChange = useCallback(
    (value: string) => {
      setCityInput(value);
      if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
      cityDebounceRef.current = setTimeout(() => {
        setFilters({ city: value.trim() || undefined });
      }, 300);
    },
    [setFilters],
  );

  // Segment pill selection
  const handleSegmentSelect = useCallback(
    (key: string) => {
      setSegment(key === 'all' ? null : key);
    },
    [setSegment],
  );

  // Customer status toggle
  const handleCustomerStatusToggle = useCallback(
    (status: CustomerStatus) => {
      const current = filters.customerStatus ?? [];
      const next = current.includes(status)
        ? current.filter((s) => s !== status)
        : [...current, status];
      setFilters({ customerStatus: next.length ? next : undefined });
    },
    [filters.customerStatus, setFilters],
  );

  const orgTypesFilter = filters.orgTypes ?? [];

  const handleOrgTypeToggle = useCallback(
    (value: string) => {
      const current = filters.orgTypes ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      setFilters({ orgTypes: next });
    },
    [filters.orgTypes, setFilters],
  );

  const handleOrgTypeClear = useCallback(() => setFilters({ orgTypes: [] }), [setFilters]);

  const customerStatusFilter = filters.customerStatus ?? [];
  const hasAnyAdvancedFilter = Boolean(
    filters.state ||
    filters.city ||
    filters.isPrivate !== undefined ||
    filters.ncesLevel ||
    filters.hasBlastLink !== undefined ||
    customerStatusFilter.length,
  );

  const handleClearAdvancedFilters = useCallback(() => {
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    setCityInput('');
    setFilters({
      state: undefined,
      city: undefined,
      isPrivate: undefined,
      ncesLevel: undefined,
      hasBlastLink: undefined,
      customerStatus: undefined,
    });
  }, [setFilters]);

  // Scope label for "Narrow X by:" header
  const scopeLabel = useMemo(() => {
    if (filters.assignedTo === 'me') return 'Mine';
    if (filters.assignedTo === 'unassigned') return 'Unassigned';
    return 'All';
  }, [filters.assignedTo]);

  // CRM-16: separate "new rep, nothing assigned" from "filtered to empty". Only
  // the former gets the friendly "switch to All" empty state.
  const isUnfilteredMineEmpty =
    filters.assignedTo === 'me' && !filters.search && !segment && !hasAnyAdvancedFilter;

  // Search placeholder reflects active scope
  const searchPlaceholder =
    scopeLabel === 'All' ? 'Search all organizations…' :
    scopeLabel === 'Mine' ? `Search within ${bookLabel(currentUser, 'Mine')}…` :
    'Search Unassigned…';

  // Build segment pills. Base counts come from OrganizationCounts (always fetched);
  // once segmentCounts loads, pillCountsOverride replaces them with in-scope values.
  // inBlast and churned have no field in OrganizationCounts — show 0 until override loads.
  const pills = counts
    ? [
        { key: 'all', label: 'All', count: counts.total },
        // inBlast and churned have no field in OrganizationCounts — use null so SegmentPills
        // renders '—' until pillCountsOverride (from /api/organizations/segment-counts) arrives.
        { key: 'inBlast', label: 'In Blast', count: null, Icon: Zap, iconClass: 'text-green-600 dark:text-green-400' },
        { key: 'churned', label: 'Churned', count: null, Icon: TrendingDown, iconClass: 'text-red-500 dark:text-red-400' },
        { key: 'neverVisited', label: 'Never Visited', count: counts.neverVisited, Icon: MapPinOff, iconClass: 'text-slate-400 dark:text-slate-500' },
        { key: 'hasCoaches', label: 'Has Coaches', count: counts.hasCoaches, Icon: UsersIcon, iconClass: 'text-blue-500 dark:text-blue-400' },
      ]
    : [];

  const activeKey = segment || 'all';

  // Build counts map for SegmentPills — maps pill keys to in-scope counts from
  // /api/organizations/segment-counts. The 'all' pill maps to scopeTotal.
  const pillCountsOverride = useMemo<Record<string, number> | undefined>(() => {
    if (!segmentCounts) return undefined;
    return {
      all: segmentCounts.scopeTotal,
      inBlast: segmentCounts.segmentCounts.inBlast,
      churned: segmentCounts.segmentCounts.churned,
      neverVisited: segmentCounts.segmentCounts.neverVisited,
      hasCoaches: segmentCounts.segmentCounts.hasCoaches,
    };
  }, [segmentCounts]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* 1. Page header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
              <Building2 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Organizations</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Schools and club teams — track engagement and Blast connectivity
              </p>
            </div>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between">
            <span className="text-red-800 dark:text-red-300">{error}</span>
            <button
              onClick={clearError}
              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* 2. Scope banner — [All] [Mine] [Unassigned] + in-scope total */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3" role="group" aria-label="Scope filter">
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Show</span>
            <div className="inline-flex rounded-full bg-gray-100 dark:bg-gray-700 p-1">
              {(
                [
                  { value: 'me', label: bookLabel(currentUser, 'Mine') },
                  { value: 'unassigned', label: 'Unassigned' },
                  { value: undefined, label: 'All' },
                ] as { value: string | undefined; label: string }[]
              ).map((opt) => {
                const isActive = (filters.assignedTo ?? undefined) === opt.value;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setFilters({ assignedTo: opt.value as 'me' | 'unassigned' | undefined })}
                    aria-pressed={isActive}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                      isActive
                        ? 'bg-[#b7ee41] text-gray-900 font-semibold'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-baseline gap-2 text-gray-600 dark:text-gray-400 text-sm">
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
              {isLoadingCounts ? '—' : (segmentCounts?.scopeTotal ?? '—')}
            </span>
            <span>organizations in scope</span>
          </div>
        </div>

        {/* 3. Search bar — placeholder reflects active scope */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="input pl-10 w-full"
          />
          {searchInput && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* 4. Segment chips — "Narrow X by:" label + pills with in-scope counts */}
        {pills.length > 0 && (
          <div>
            <div
              id="segment-pills-label"
              className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2"
            >
              Narrow {scopeLabel} by:
            </div>
            <SegmentPills
              pills={pills}
              activeKey={activeKey}
              onSelect={handleSegmentSelect}
              counts={pillCountsOverride}
              isLoadingCounts={isLoadingCounts}
              aria-labelledby="segment-pills-label"
            />
          </div>
        )}

        {/* 4b. Type filter — always-visible primary axis (CRM-72) */}
        <OrgTypeFilter selected={orgTypesFilter} onToggle={handleOrgTypeToggle} onClear={handleOrgTypeClear} />

        {/* 5. Advanced filters — collapsed disclosure, auto-expands if any value set at mount */}
        <div
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl w-fit max-w-full"
        >
          <button
            type="button"
            aria-expanded={isAdvancedOpen}
            aria-controls={advancedPanelId}
            onClick={() => setIsAdvancedOpen((o) => !o)}
            className="cursor-pointer select-none px-4 py-3 text-sm text-gray-700 dark:text-gray-300 font-medium flex items-center justify-between gap-6 w-full text-left"
          >
            <span>Advanced filters · status, city, state</span>
            <span className="flex items-center gap-2">
              {hasAnyAdvancedFilter && (
                <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-medium">
                  active
                </span>
              )}
              <ChevronDown
                data-testid="advanced-filters-chevron"
                aria-hidden="true"
                className={`h-4 w-4 text-gray-400 transition-transform ${isAdvancedOpen ? 'rotate-180' : ''}`}
              />
            </span>
          </button>
          <div id={advancedPanelId} className={isAdvancedOpen ? 'p-4 pt-0' : 'px-4 invisible h-0 overflow-hidden'}>
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                {pagination && !isLoading && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-medium">
                    {pagination.totalCount.toLocaleString()} {pagination.totalCount === 1 ? 'organization' : 'organizations'} match
                  </span>
                )}
              </div>
              {hasAnyAdvancedFilter && (
                <button
                  type="button"
                  onClick={handleClearAdvancedFilters}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                >
                  Clear filters
                </button>
              )}
            </div>

            {/* Customer status */}
            <div>
              <span className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                Customer status <span className="text-gray-400 font-normal">— click chips to filter</span>
              </span>
              <div className="flex flex-wrap gap-1.5">
                {VALID_STATES.map((status) => {
                  const isOn = customerStatusFilter.includes(status);
                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() => handleCustomerStatusToggle(status)}
                      aria-pressed={isOn}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        isOn
                          ? 'bg-purple-600 dark:bg-purple-500 border-purple-600 dark:border-purple-500 text-white'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-purple-400 dark:hover:border-purple-500'
                      }`}
                    >
                      {CUSTOMER_STATUS_LABELS[status]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* City filter — free text, available to all roles (CRM-99) */}
            <div className="mt-3">
              <label htmlFor="org-city-filter" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                City
              </label>
              <input
                id="org-city-filter"
                type="text"
                value={cityInput}
                onChange={(e) => handleCityChange(e.target.value)}
                placeholder="e.g. Lincoln"
                maxLength={100}
                className="rounded-lg bg-gray-100 dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* State filter — every role. The API scopes the states list to every active filter except State
                (CRM-158), and a selected state is always kept visible so it can be cleared. */}
            {(availableStates.length > 0 || !!filters.state) && (
              <div className="mt-3">
                <label htmlFor="org-state-filter" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  State
                </label>
                <select
                  id="org-state-filter"
                  value={filters.state || ''}
                  onChange={(e) => setFilters({ state: e.target.value || undefined })}
                  className="rounded-lg bg-gray-100 dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
                >
                  <option value="">All States</option>
                  {stateOptions.map((st) => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* 6. Card list */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
          </div>
        ) : organizations.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
            {isUnfilteredMineEmpty ? (
              <>
                <p className="text-lg font-medium">Nothing assigned to you yet.</p>
                <button
                  type="button"
                  onClick={() => setFilters({ assignedTo: undefined })}
                  className="btn-secondary text-sm mt-4"
                >
                  Show all organizations
                </button>
              </>
            ) : (
              <>
                <p className="text-lg font-medium">No organizations found</p>
                <p className="text-sm mt-1">Try adjusting your filters or search terms.</p>
                {(filters.search || filters.state || filters.city || segment) && (
                  <button
                    onClick={() => {
                      setSegment(null);
                      clearFilters();
                    }}
                    className="mt-4 text-purple-600 dark:text-purple-400 hover:underline text-sm font-medium"
                  >
                    Clear all filters
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {organizations.map((org) => (
              <OrganizationCard
                key={org.id}
                organization={org}
                coachCount={(org as { _count?: { people?: number } })._count?.people || 0}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Page {pagination.page} of {pagination.totalPages} ({pagination.totalCount} total)
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(pagination.page - 1)}
                disabled={!pagination.hasPrevPage}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(pagination.page + 1)}
                disabled={!pagination.hasNextPage}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
